import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { MODULES } from "../../config/modules";
import { PageHeader, StatusBadge } from "../../components/bits";
import { ArtifactForm } from "../../components/ArtifactTable";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Trash2, ChevronRight, ChevronLeft } from "lucide-react";

const WF_FIELDS = [
  { key: "name", label: "Workflow name", type: "text" },
  { key: "stages", label: "Stages (comma separated, e.g. Draft, Review, Sign-off)", type: "text" },
];
const DELIV_FIELDS = [
  { key: "title", label: "Deliverable", type: "text" },
  { key: "owner", label: "Owner", type: "text" },
  { key: "dueDate", label: "Due", type: "date" },
];

export default function WorkflowsPage() {
  const { projectId } = useParams();
  const [workflows, setWorkflows] = useState([]);
  const [items, setItems] = useState([]);
  const [wfOpen, setWfOpen] = useState(false);
  const [dOpen, setDOpen] = useState(false);
  const [wfForm, setWfForm] = useState({});
  const [dForm, setDForm] = useState({});

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/workflows`).then((r) => setWorkflows(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/artifacts/deliverables`).then((r) => setItems(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const stagesOf = (wfId) => {
    const wf = workflows.find((w) => w.id === wfId);
    return (wf?.stages || "Draft, Review, Sign-off").split(",").map((s) => s.trim()).filter(Boolean);
  };

  const saveWf = async () => {
    try { await api.post(`/projects/${projectId}/artifacts/workflows`, wfForm); setWfOpen(false); load(); toast.success("Workflow saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveDeliv = async () => {
    try {
      const wfId = dForm.workflowId || workflows[0]?.id;
      await api.post(`/projects/${projectId}/artifacts/deliverables`, { ...dForm, workflowId: wfId, stageIndex: 0 });
      setDOpen(false); load(); toast.success("Deliverable added");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (item, delta) => {
    const stages = stagesOf(item.workflowId);
    const next = Math.min(Math.max((item.stageIndex || 0) + delta, 0), stages.length - 1);
    await api.put(`/projects/${projectId}/artifacts/deliverables/${item.id}`, { ...item, stageIndex: next });
    load();
  };

  return (
    <div data-testid="workflows-page">
      <PageHeader overline="Governance · Configurable workflows" title="Workflows & Deliverables"
        actions={
          <>
            <Button data-testid="add-workflow-btn" variant="outline" className="rounded-none" onClick={() => { setWfForm({ stages: "Draft, Review, Sign-off" }); setWfOpen(true); }}><Plus className="h-4 w-4 mr-1" />Workflow</Button>
            <Button data-testid="add-deliverable-btn" className="rounded-none" onClick={() => { setDForm({}); setDOpen(true); }} disabled={workflows.length === 0}><Plus className="h-4 w-4 mr-1" />Deliverable</Button>
          </>
        } />
      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        <div className="border border-border bg-card h-fit">
          <div className="px-4 py-2.5 border-b border-border overline-label">Workflow definitions</div>
          {workflows.map((w) => (
            <div key={w.id} data-testid="workflow-row" className="px-4 py-2 border-b border-border last:border-b-0 flex justify-between items-start">
              <div>
                <div className="font-semibold text-sm">{w.name}</div>
                <div className="text-xs text-muted-foreground">{w.stages}</div>
              </div>
              <button onClick={async () => { await api.delete(`/projects/${projectId}/artifacts/workflows/${w.id}`); load(); }} className="p-1 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
          {workflows.length === 0 && <div className="px-4 py-4 text-xs text-muted-foreground">Define a workflow, e.g. Draft → Review → Sign-off.</div>}
        </div>
        <div className="border border-border bg-card overflow-auto">
          <table className="w-full data-table text-sm">
            <thead><tr><th>Deliverable</th><th>Owner</th><th>Due</th><th>Workflow stage</th><th></th></tr></thead>
            <tbody>
              {items.map((it) => {
                const stages = stagesOf(it.workflowId);
                const idx = Math.min(it.stageIndex || 0, stages.length - 1);
                return (
                  <tr key={it.id} data-testid="deliverable-row">
                    <td className="font-medium">{it.title}</td>
                    <td>{it.owner}</td>
                    <td className="text-xs">{it.dueDate || "—"}</td>
                    <td>
                      <div className="flex items-center gap-1 flex-wrap">
                        {stages.map((s, i) => (
                          <span key={s} className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 border rounded-sm ${i === idx ? "border-[#1E6FE8] text-[#1E6FE8] bg-[#1E6FE8]/5 font-bold" : i < idx ? "border-[#00C781] text-[#007a4f]" : "border-border text-muted-foreground"}`}>{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <button data-testid="stage-back-btn" onClick={() => move(it, -1)} className="p-1 hover:text-[#1E6FE8]" disabled={idx === 0}><ChevronLeft className="h-4 w-4" /></button>
                      <button data-testid="stage-advance-btn" onClick={() => move(it, 1)} className="p-1 hover:text-[#1E6FE8]"><ChevronRight className="h-4 w-4" /></button>
                      <button onClick={async () => { await api.delete(`/projects/${projectId}/artifacts/deliverables/${it.id}`); load(); }} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No deliverables yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={wfOpen} onOpenChange={setWfOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-display">New Workflow</DialogTitle></DialogHeader>
          <ArtifactForm fields={WF_FIELDS} value={wfForm} onChange={setWfForm} />
          <Button data-testid="save-workflow-btn" className="rounded-none" onClick={saveWf} disabled={!wfForm.name}>Save</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-display">New Deliverable</DialogTitle></DialogHeader>
          <ArtifactForm fields={DELIV_FIELDS} value={dForm} onChange={setDForm} />
          <label className="text-xs uppercase tracking-wide font-medium">Workflow</label>
          <select data-testid="deliverable-workflow-select" className="w-full border border-input px-3 py-2 text-sm bg-background" value={dForm.workflowId || ""} onChange={(e) => setDForm({ ...dForm, workflowId: e.target.value })}>
            {workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <Button data-testid="save-deliverable-btn" className="rounded-none" onClick={saveDeliv} disabled={!dForm.title}>Save</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
